import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';
import { sendChatMessageNotification } from '../../../hooks/useNotifications';

interface GroupScheduleSlot {
  id: string;
  schedule_id: string;
  group_id: string;
  title: string;
  start_datetime: string;
  end_datetime: string;
  created_by: string;
  color: string;
  created_at: string;
  memo?: string;
  creator_name?: string;
}

interface Message {
  id: string;
  user_id: string;
  user_name: string;
  message: string;
  created_at: string;
}

interface GroupScheduleCalendarProps {
  groupId: string;
  scheduleId: string;
  calendarName: string;
  onClose: () => void;
  onUpdateCalendarName: (newName: string) => void;
}

const COLORS = [
  { value: '#14B8A6', label: 'Teal' },
  { value: '#10B981', label: 'Emerald' },
  { value: '#F59E0B', label: 'Amber' },
  { value: '#EC4899', label: 'Pink' },
  { value: '#EF4444', label: 'Red' },
  { value: '#6366F1', label: 'Indigo' },
  { value: '#F97316', label: 'Orange' },
];

async function fetchUsernamesByAuthIds(
  authIds: string[],
  existingMap: Map<string, string>
): Promise<Map<string, string>> {
  const updatedMap = new Map(existingMap);
  const missing = authIds.filter((id) => id && !updatedMap.has(id));
  if (missing.length === 0) return updatedMap;

  const { data } = await supabase
    .from('user_profiles')
    .select('user_id, username')
    .in('user_id', missing);

  data?.forEach((p) => {
    updatedMap.set(p.user_id, p.username || '不明');
  });

  return updatedMap;
}

export default function GroupScheduleCalendar({
  groupId,
  scheduleId,
  calendarName,
  onClose,
  onUpdateCalendarName,
}: GroupScheduleCalendarProps) {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [slots, setSlots] = useState<GroupScheduleSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<GroupScheduleSlot | null>(null);
  const [newCalendarName, setNewCalendarName] = useState(calendarName);
  const [errorMsg, setErrorMsg] = useState('');
  const [addErrorMsg, setAddErrorMsg] = useState('');
  const [editErrorMsg, setEditErrorMsg] = useState('');
  const [nameErrorMsg, setNameErrorMsg] = useState('');
  const [newSlot, setNewSlot] = useState({
    title: '',
    date: '',
    startHour: '9',
    startMinute: '0',
    endHour: '10',
    endMinute: '0',
    color: '#14B8A6',
    memo: '',
  });
  const [editSlot, setEditSlot] = useState({
    title: '',
    date: '',
    startHour: '9',
    startMinute: '0',
    endHour: '10',
    endMinute: '0',
    color: '#14B8A6',
    memo: '',
  });

  const [profileMap, setProfileMap] = useState<Map<string, string>>(new Map());
  const [memberCount, setMemberCount] = useState(0);
  const [myProfileId, setMyProfileId] = useState<string | null>(null);

  const [showChat, setShowChat] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [readStatus, setReadStatus] = useState<Record<string, string[]>>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const showChatRef = useRef(false);

  // showChatの最新値をrefで追跡（リアルタイムコールバック内で参照するため）
  useEffect(() => {
    showChatRef.current = showChat;
  }, [showChat]);

  useEffect(() => {
    if (!user) return;
    const fetchMyProfileId = async () => {
      const { data } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
      if (data) setMyProfileId(data.id);
    };
    fetchMyProfileId();
  }, [user]);

  const fetchSlots = useCallback(async () => {
    try {
      setLoading(true);
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59);

      const { data: slotsData, error: slotsError } = await supabase
        .from('group_schedule_slots')
        .select('*')
        .eq('schedule_id', scheduleId)
        .gte('start_datetime', startOfMonth.toISOString())
        .lte('start_datetime', endOfMonth.toISOString())
        .order('start_datetime', { ascending: true });

      if (slotsError) throw slotsError;

      const raw = slotsData || [];
      const userIds = [...new Set(raw.map((s) => s.created_by))];
      const updatedMap = await fetchUsernamesByAuthIds(userIds, profileMap);
      setProfileMap(updatedMap);

      const enriched = raw.map((slot) => ({
        ...slot,
        creator_name: updatedMap.get(slot.created_by) || '不明',
      }));
      setSlots(enriched);
    } catch (error) {
      console.error('予定の取得に失敗しました:', error);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleId, currentDate]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  useEffect(() => {
    const channel = supabase
      .channel(`schedule_slots_${scheduleId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'group_schedule_slots', filter: `schedule_id=eq.${scheduleId}` },
        async (payload) => {
          if (payload.eventType === 'INSERT') {
            const newRecord = payload.new as GroupScheduleSlot;
            const updatedMap = await fetchUsernamesByAuthIds([newRecord.created_by], profileMap);
            setProfileMap(updatedMap);
            const enriched = { ...newRecord, creator_name: updatedMap.get(newRecord.created_by) || '不明' };
            setSlots((prev) => {
              const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
              const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59);
              const slotDate = new Date(newRecord.start_datetime);
              if (slotDate >= startOfMonth && slotDate <= endOfMonth) {
                return [...prev, enriched].sort(
                  (a, b) => new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime()
                );
              }
              return prev;
            });
          } else if (payload.eventType === 'DELETE') {
            setSlots((prev) => prev.filter((s) => s.id !== payload.old.id));
          } else if (payload.eventType === 'UPDATE') {
            const updated = payload.new as GroupScheduleSlot;
            const updatedMap = await fetchUsernamesByAuthIds([updated.created_by], profileMap);
            setProfileMap(updatedMap);
            const enriched = { ...updated, creator_name: updatedMap.get(updated.created_by) || '不明' };
            setSlots((prev) => prev.map((s) => (s.id === updated.id ? enriched : s)));
          }
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleId, currentDate]);

  useEffect(() => {
    if (!scheduleId || !user) return;

    const fetchMessages = async () => {
      const { data: messagesData, error: messagesError } = await supabase
        .from('group_chat_messages')
        .select('*')
        .eq('schedule_id', scheduleId)
        .order('created_at', { ascending: true });

      if (messagesError) {
        console.error('メッセージ取得エラー:', messagesError);
        return;
      }

      const msgs = messagesData || [];
      const senderProfileIds = [...new Set(msgs.map((m) => m.user_id))];
      if (senderProfileIds.length > 0) {
        const { data: profiles } = await supabase
          .from('user_profiles')
          .select('id, username')
          .in('id', senderProfileIds);
        const updatedMap = new Map(profileMap);
        profiles?.forEach((p) => updatedMap.set(p.id, p.username || '不明'));
        setProfileMap(updatedMap);
      }

      const msgIds = msgs.map((m) => m.id);
      const readByMessage: Record<string, string[]> = {};
      if (msgIds.length > 0) {
        const { data: readData } = await supabase
          .from('group_chat_read_status')
          .select('message_id, user_id')
          .in('message_id', msgIds);

        readData?.forEach((r) => {
          if (!readByMessage[r.message_id]) readByMessage[r.message_id] = [];
          readByMessage[r.message_id].push(r.user_id);
        });
      }
      setReadStatus(readByMessage);
      setMessages(msgs);

      if (!myProfileId) return;
      const unreadMsgs = msgs.filter(
        (m) => m.user_id !== myProfileId && !readByMessage[m.id]?.includes(myProfileId)
      );
      for (const msg of unreadMsgs) {
        await supabase.from('group_chat_read_status').upsert(
          { message_id: msg.id, user_id: myProfileId },
          { onConflict: 'message_id,user_id' }
        );
      }
    };

    fetchMessages();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleId, user, myProfileId]);

  useEffect(() => {
    if (!scheduleId || !user) return;

    const channel = supabase
      .channel(`chat:${scheduleId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'group_chat_messages', filter: `schedule_id=eq.${scheduleId}` },
        async (payload) => {
          const newMsg = payload.new as Message;

          const { data: profile } = await supabase
            .from('user_profiles')
            .select('id, username')
            .eq('id', newMsg.user_id)
            .maybeSingle();
          if (profile) {
            setProfileMap((prev) => new Map(prev).set(profile.id, profile.username || '不明'));
          }

          setMessages((prev) => {
            if (prev.find((m) => m.id === newMsg.id)) return prev;
            return [...prev, newMsg];
          });

          if (myProfileId && newMsg.user_id !== myProfileId) {
            // チャットが表示中（PC常時 or スマホでshowChat=true）なら即既読
            const isChatVisible = showChatRef.current || window.innerWidth >= 640;
            if (isChatVisible) {
              await supabase.from('group_chat_read_status').upsert(
                { message_id: newMsg.id, user_id: myProfileId },
                { onConflict: 'message_id,user_id' }
              );
              setReadStatus((prev) => ({
                ...prev,
                [newMsg.id]: [...(prev[newMsg.id] || []), myProfileId],
              }));
            } else {
              // チャット非表示なら未読カウントを増やす
              setUnreadCount((prev) => prev + 1);
            }
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'group_chat_messages', filter: `schedule_id=eq.${scheduleId}` },
        (payload) => {
          setMessages((prev) => prev.filter((m) => m.id !== payload.old.id));
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'group_chat_read_status' },
        (payload) => {
          const r = payload.new as { message_id: string; user_id: string };
          setReadStatus((prev) => ({
            ...prev,
            [r.message_id]: [...(prev[r.message_id] || []), r.user_id],
          }));
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [scheduleId, user, myProfileId]);

  // チャットを開いたとき：未読メッセージを一括既読 + スクロール
  useEffect(() => {
    if (!showChat || !myProfileId) return;
    setUnreadCount(0);
    // 未読メッセージを既読にする
    const unreadMsgs = messages.filter(
      m => m.user_id !== myProfileId && !(readStatus[m.id] || []).includes(myProfileId)
    );
    if (unreadMsgs.length > 0) {
      Promise.all(
        unreadMsgs.map(msg =>
          supabase.from('group_chat_read_status').upsert(
            { message_id: msg.id, user_id: myProfileId },
            { onConflict: 'message_id,user_id' }
          )
        )
      ).then(() => {
        setReadStatus(prev => {
          const next = { ...prev };
          unreadMsgs.forEach(msg => {
            next[msg.id] = [...(next[msg.id] || []), myProfileId!];
          });
          return next;
        });
      });
    }
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showChat]);

  // 新着メッセージが届いたとき：チャットが開いていれば即既読
  useEffect(() => {
    if (!myProfileId || messages.length === 0) return;
    // PC版は常時チャット表示なので常に既読処理、スマホ版はshowChatがtrueのとき
    const isChatVisible = showChat || window.innerWidth >= 640;
    if (!isChatVisible) return;

    const latestMsg = messages[messages.length - 1];
    if (!latestMsg || latestMsg.user_id === myProfileId) return;
    if ((readStatus[latestMsg.id] || []).includes(myProfileId)) return;

    supabase.from('group_chat_read_status').upsert(
      { message_id: latestMsg.id, user_id: myProfileId },
      { onConflict: 'message_id,user_id' }
    ).then(() => {
      setReadStatus(prev => ({
        ...prev,
        [latestMsg.id]: [...(prev[latestMsg.id] || []), myProfileId!],
      }));
    });

    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const getReadCount = (messageId: string, senderId: string) => {
    const readers = readStatus[messageId] || [];
    return readers.filter((id) => id !== senderId).length;
  };

  useEffect(() => {
    if (!groupId) return;
    const fetchMemberCount = async () => {
      const { data, error } = await supabase
        .from('study_group_members')
        .select('user_id')
        .eq('group_id', groupId);
      if (!error && data) setMemberCount(data.length - 1);
    };
    fetchMemberCount();
  }, [groupId]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !user || !scheduleId || !myProfileId) return;

    setSendingMessage(true);
    try {
      const { data, error } = await supabase
        .from('group_chat_messages')
        .insert({
          schedule_id: scheduleId,
          group_id: groupId,
          user_id: myProfileId,
          message: newMessage.trim(),
        })
        .select()
        .maybeSingle();

      if (error) {
        console.error('メッセージ送信エラー:', JSON.stringify(error));
        return;
      }

      if (data) {
        await supabase.from('group_chat_read_status').upsert(
          { message_id: data.id, user_id: myProfileId },
          { onConflict: 'message_id,user_id' }
        );
        setReadStatus((prev) => ({ ...prev, [data.id]: [myProfileId] }));
        setMessages((prev) => {
          if (prev.find((m) => m.id === data.id)) return prev;
          return [...prev, data];
        });

        // グループメンバーに通知を送る（auth user_idを使用）
        const { data: members } = await supabase
          .from('study_group_members')
          .select('user_id')
          .eq('group_id', groupId);
        const memberAuthIds = (members || []).map((m: { user_id: string }) => m.user_id);
        if (memberAuthIds.length > 0) {
          // グループ名を取得
          const { data: groupData } = await supabase
            .from('study_groups')
            .select('name')
            .eq('id', groupId)
            .maybeSingle();
          const groupName = groupData?.name || 'グループ';
          await sendChatMessageNotification(groupId, groupName, memberAuthIds, user.id);
        }
      }

      setNewMessage('');
    } catch (err) {
      console.error('メッセージ送信エラー:', err);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleDeleteMessage = async (messageId: string, createdBy: string) => {
    if (!myProfileId || myProfileId !== createdBy) return;
    // 即時ローカル反映（削除ボタンを押した瞬間に画面から消す）
    setMessages((prev) => prev.filter((m) => m.id !== messageId));
    try {
      const { error } = await supabase.from('group_chat_messages').delete().eq('id', messageId);
      if (error) {
        // 削除失敗時は元に戻す（再取得）
        console.error('メッセージの削除に失敗しました:', error);
        const { data: messagesData } = await supabase
          .from('group_chat_messages')
          .select('*')
          .eq('schedule_id', scheduleId)
          .order('created_at', { ascending: true });
        if (messagesData) setMessages(messagesData);
      }
    } catch (error) {
      console.error('メッセージの削除に失敗しました:', error);
    }
  };

  const handleAddSlot = async () => {
    setAddErrorMsg('');
    if (!user || !newSlot.title || !newSlot.date || !newSlot.startHour || !newSlot.endHour) {
      setAddErrorMsg('すべての項目を入力してください');
      return;
    }
    const startTimeStr = `${String(newSlot.startHour).padStart(2, '0')}:${String(newSlot.startMinute).padStart(2, '0')}`;
    const endTimeStr = `${String(newSlot.endHour).padStart(2, '0')}:${String(newSlot.endMinute).padStart(2, '0')}`;
    if (startTimeStr >= endTimeStr) {
      setAddErrorMsg('終了時刻は開始時刻より後に設定してください');
      return;
    }
    try {
      const { data, error } = await supabase.from('group_schedule_slots').insert({
        schedule_id: scheduleId,
        group_id: groupId,
        title: newSlot.title,
        start_datetime: `${newSlot.date}T${startTimeStr}:00+09:00`,
        end_datetime: `${newSlot.date}T${endTimeStr}:00+09:00`,
        created_by: user.id,
        color: newSlot.color,
        memo: newSlot.memo || null,
      }).select().maybeSingle();
      if (error) throw error;

      // ローカル状態に即時反映
      if (data) {
        const updatedMap = await fetchUsernamesByAuthIds([data.created_by], profileMap);
        setProfileMap(updatedMap);
        const enriched = { ...data, creator_name: updatedMap.get(data.created_by) || '不明' };
        setSlots((prev) => {
          const exists = prev.find((s) => s.id === enriched.id);
          if (exists) return prev;
          return [...prev, enriched].sort(
            (a, b) => new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime()
          );
        });
      }

      setShowAddModal(false);
      setNewSlot({ title: '', date: '', startHour: '9', startMinute: '0', endHour: '10', endMinute: '0', color: '#14B8A6', memo: '' });
    } catch (error) {
      console.error('予定の追加に失敗しました:', error);
      setAddErrorMsg('予定の追加に失敗しました。もう一度お試しください。');
    }
  };

  const openEditModal = (slot: GroupScheduleSlot) => {
    const start = new Date(slot.start_datetime);
    const end = new Date(slot.end_datetime);
    const y = start.getFullYear();
    const m = String(start.getMonth() + 1).padStart(2, '0');
    const d = String(start.getDate()).padStart(2, '0');
    setEditSlot({
      title: slot.title,
      date: `${y}-${m}-${d}`,
      startHour: String(start.getHours()),
      startMinute: String(start.getMinutes()),
      endHour: String(end.getHours()),
      endMinute: String(end.getMinutes()),
      color: slot.color,
      memo: slot.memo || '',
    });
    setEditErrorMsg('');
    setShowDetailModal(false);
    setShowEditModal(true);
  };

  const handleEditSlot = async () => {
    if (!selectedSlot || !user) return;
    setEditErrorMsg('');
    if (!editSlot.title || !editSlot.date) {
      setEditErrorMsg('タイトルと日付を入力してください');
      return;
    }
    const startTimeStr = `${String(editSlot.startHour).padStart(2, '0')}:${String(editSlot.startMinute).padStart(2, '0')}`;
    const endTimeStr = `${String(editSlot.endHour).padStart(2, '0')}:${String(editSlot.endMinute).padStart(2, '0')}`;
    if (startTimeStr >= endTimeStr) {
      setEditErrorMsg('終了時刻は開始時刻より後に設定してください');
      return;
    }
    try {
      const updates = {
        title: editSlot.title,
        start_datetime: `${editSlot.date}T${startTimeStr}:00+09:00`,
        end_datetime: `${editSlot.date}T${endTimeStr}:00+09:00`,
        color: editSlot.color,
        memo: editSlot.memo || null,
      };
      const { error } = await supabase
        .from('group_schedule_slots')
        .update(updates)
        .eq('id', selectedSlot.id);
      if (error) throw error;

      // ローカル状態に即時反映
      const updatedSlot: GroupScheduleSlot = {
        ...selectedSlot,
        ...updates,
        creator_name: selectedSlot.creator_name,
      };
      setSlots((prev) =>
        prev
          .map((s) => (s.id === selectedSlot.id ? updatedSlot : s))
          .sort((a, b) => new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime())
      );

      setShowEditModal(false);
      setSelectedSlot(null);
    } catch (error) {
      console.error('予定の編集に失敗しました:', error);
      setEditErrorMsg('予定の編集に失敗しました。もう一度お試しください。');
    }
  };

  const handleDeleteSlot = async (slotId: string, createdBy: string) => {
    if (!user || user.id !== createdBy) {
      setErrorMsg('自分が追加した予定のみ削除できます');
      setTimeout(() => setErrorMsg(''), 3000);
      return;
    }
    try {
      const { error } = await supabase.from('group_schedule_slots').delete().eq('id', slotId);
      if (error) throw error;

      // ローカル状態に即時反映
      setSlots((prev) => prev.filter((s) => s.id !== slotId));
      setShowDetailModal(false);
      setSelectedSlot(null);
    } catch (error) {
      console.error('予定の削除に失敗しました:', error);
    }
  };

  const handleUpdateCalendarName = async () => {
    setNameErrorMsg('');
    if (!newCalendarName.trim()) {
      setNameErrorMsg('カレンダー名を入力してください');
      return;
    }
    try {
      const { error } = await supabase
        .from('group_schedules')
        .update({ calendar_name: newCalendarName.trim() })
        .eq('id', scheduleId);
      if (error) throw error;
      
      // ローカル状態を即座に更新
      setLocalCalendarName(newCalendarName.trim());
      onUpdateCalendarName(newCalendarName.trim());
      setShowEditNameModal(false);
    } catch (error) {
      console.error('カレンダー名の更新に失敗しました:', error);
      setNameErrorMsg('更新に失敗しました。もう一度お試しください。');
    }
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDayOfWeek = firstDay.getDay();
    const days: (Date | null)[] = [];
    for (let i = 0; i < startDayOfWeek; i++) days.push(null);
    for (let i = 1; i <= lastDay.getDate(); i++) days.push(new Date(year, month, i));
    return days;
  };

  const getSlotsForDate = (date: Date | null) => {
    if (!date) return [];
    return slots.filter((slot) => {
      const slotDate = new Date(slot.start_datetime);
      return (
        slotDate.getFullYear() === date.getFullYear() &&
        slotDate.getMonth() === date.getMonth() &&
        slotDate.getDate() === date.getDate()
      );
    });
  };

  const formatTime = (datetime: string) =>
    new Date(datetime).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });

  const formatDate = (datetime: string) =>
    new Date(datetime).toLocaleDateString('ja-JP', {
      year: 'numeric', month: 'long', day: 'numeric', weekday: 'short',
    });

  const openAddModal = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    setNewSlot((prev) => ({ ...prev, date: `${y}-${m}-${d}` }));
    setAddErrorMsg('');
    setShowAddModal(true);
  };

  const isMySlot = (slot: GroupScheduleSlot) => user?.id === slot.created_by;

  const days = getDaysInMonth();
  const weekDays = ['日', '月', '火', '水', '木', '金', '土'];

  // 時刻セレクト共通コンポーネント
  const TimeSelect = ({
    hourVal, minuteVal, onHourChange, onMinuteChange,
  }: {
    hourVal: string; minuteVal: string;
    onHourChange: (v: string) => void; onMinuteChange: (v: string) => void;
  }) => (
    <div className="flex items-center gap-2">
      <select
        value={hourVal}
        onChange={(e) => onHourChange(e.target.value)}
        className="flex-1 px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white text-sm cursor-pointer"
      >
        {Array.from({ length: 24 }, (_, i) => i + 1).map((h) => (
          <option key={h} value={String(h)}>{String(h).padStart(2, '0')}時</option>
        ))}
      </select>
      <span className="text-gray-500 dark:text-gray-400 font-bold shrink-0">:</span>
      <select
        value={minuteVal}
        onChange={(e) => onMinuteChange(e.target.value)}
        className="flex-1 px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white text-sm cursor-pointer"
      >
        {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((m) => (
          <option key={m} value={String(m)}>{String(m).padStart(2, '0')}分</option>
        ))}
      </select>
    </div>
  );

  // カレンダー名のローカル状態を追加
  const [localCalendarName, setLocalCalendarName] = useState(calendarName);

  // カレンダー名が変更されたら同期
  useEffect(() => {
    setLocalCalendarName(calendarName);
    setNewCalendarName(calendarName);
  }, [calendarName]);

  // group_schedules テーブルのリアルタイム更新を監視
  useEffect(() => {
    const channel = supabase
      .channel(`schedule_name_${scheduleId}`)
      .on(
        'postgres_changes',
        { 
          event: 'UPDATE', 
          schema: 'public', 
          table: 'group_schedules', 
          filter: `id=eq.${scheduleId}` 
        },
        (payload) => {
          const updated = payload.new as { id: string; calendar_name: string };
          if (updated.calendar_name) {
            setLocalCalendarName(updated.calendar_name);
            onUpdateCalendarName(updated.calendar_name);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [scheduleId, onUpdateCalendarName]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white dark:bg-gray-800 rounded-none sm:rounded-2xl shadow-2xl w-full max-w-7xl h-full sm:max-h-[95vh] overflow-hidden flex flex-col">

        {/* ヘッダー */}
        <div className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white p-3 sm:p-5 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <i className="ri-calendar-check-line text-lg sm:text-xl shrink-0"></i>
              <h2 className="text-base sm:text-xl font-bold truncate">{localCalendarName}</h2>
              <button
                onClick={() => { setNewCalendarName(localCalendarName); setNameErrorMsg(''); setShowEditNameModal(true); }}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors shrink-0"
                title="カレンダー名を編集"
              >
                <i className="ri-edit-line text-sm sm:text-base"></i>
              </button>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setShowChat(!showChat)}
                className="sm:hidden relative p-2 hover:bg-white/20 rounded-lg transition-colors"
                title="チャット"
              >
                <i className="ri-chat-3-line text-xl"></i>
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 bg-white/20 rounded-full text-xs">
                <span className="w-1.5 h-1.5 bg-green-300 rounded-full animate-pulse"></span>
                リアルタイム同期中
              </div>
              <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
          </div>
          <p className="text-white/80 text-xs mt-1 hidden sm:block">グループメンバーの予定がリアルタイムで反映されます</p>
        </div>

        {errorMsg && (
          <div className="mx-3 sm:mx-4 mt-2 sm:mt-3 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg text-sm text-red-600 dark:text-red-400 shrink-0">
            {errorMsg}
          </div>
        )}

        {/* メインコンテンツ */}
        <div className="flex-1 flex overflow-hidden relative">

          {/* カレンダーエリア */}
          <div className={`flex-1 flex flex-col overflow-hidden ${showChat ? 'hidden sm:flex' : 'flex'}`}>
            <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border-b dark:border-gray-700 shrink-0">
              <button
                onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-xl text-gray-600 dark:text-gray-300"></i>
              </button>
              <h3 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white">
                {currentDate.getFullYear()}年 {currentDate.getMonth() + 1}月
              </h3>
              <button
                onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-xl text-gray-600 dark:text-gray-300"></i>
              </button>
            </div>

            <div className="flex-1 overflow-auto p-2 sm:p-4">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-10 w-10 border-4 border-teal-500 border-t-transparent"></div>
                </div>
              ) : (
                <>
                  {/* スマホ用コンパクトカレンダー */}
                  <div className="sm:hidden">
                    <div className="grid grid-cols-7 gap-0.5 mb-1">
                      {weekDays.map((day, index) => (
                        <div
                          key={day}
                          className={`text-center font-bold py-1 text-xs ${
                            index === 0 ? 'text-red-500' : index === 6 ? 'text-teal-500' : 'text-gray-500 dark:text-gray-400'
                          }`}
                        >
                          {day}
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-0.5">
                      {days.map((date, index) => {
                        const daySlots = getSlotsForDate(date);
                        const isToday = date && date.toDateString() === new Date().toDateString();
                        const isSunday = date && date.getDay() === 0;
                        const isSaturday = date && date.getDay() === 6;
                        return (
                          <div
                            key={index}
                            className={`aspect-square flex flex-col items-center justify-start pt-1 rounded-lg transition-colors ${
                              date
                                ? 'bg-white dark:bg-gray-750 border border-gray-100 dark:border-gray-700 active:bg-teal-50'
                                : 'bg-transparent border-transparent'
                            } ${isToday ? 'ring-2 ring-teal-500 border-teal-300' : ''}`}
                            onClick={() => date && openAddModal(date)}
                          >
                            {date && (
                              <>
                                <span
                                  className={`text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full ${
                                    isToday
                                      ? 'bg-teal-500 text-white'
                                      : isSunday
                                      ? 'text-red-500'
                                      : isSaturday
                                      ? 'text-teal-600 dark:text-teal-400'
                                      : 'text-gray-700 dark:text-gray-300'
                                  }`}
                                >
                                  {date.getDate()}
                                </span>
                                {daySlots.length > 0 && (
                                  <div className="flex gap-0.5 mt-0.5 flex-wrap justify-center">
                                    {daySlots.slice(0, 3).map((slot) => (
                                      <div
                                        key={slot.id}
                                        className="w-1.5 h-1.5 rounded-full"
                                        style={{ backgroundColor: slot.color }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedSlot(slot);
                                          setShowDetailModal(true);
                                        }}
                                      />
                                    ))}
                                    {daySlots.length > 3 && (
                                      <span className="text-[8px] text-gray-400 leading-none">+{daySlots.length - 3}</span>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {slots.length > 0 && (
                      <div className="mt-3 space-y-1.5">
                        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 px-1">今月の予定</p>
                        {slots.map((slot) => (
                          <div
                            key={slot.id}
                            onClick={() => { setSelectedSlot(slot); setShowDetailModal(true); }}
                            className="flex items-center gap-2 p-2 bg-white dark:bg-gray-750 rounded-lg border border-gray-100 dark:border-gray-700 cursor-pointer active:bg-gray-50"
                          >
                            <div className="w-2 h-full min-h-[32px] rounded-full shrink-0" style={{ backgroundColor: slot.color }}></div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">{slot.title}</p>
                              <p className="text-[10px] text-gray-500 dark:text-gray-400">
                                {new Date(slot.start_datetime).toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' })}
                                　{formatTime(slot.start_datetime)}〜{formatTime(slot.end_datetime)}
                              </p>
                            </div>
                            <span className="text-[10px] text-gray-400 shrink-0">{slot.creator_name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* PC用カレンダー */}
                  <div className="hidden sm:block min-w-[700px]">
                    <div className="grid grid-cols-7 gap-1.5 mb-1.5">
                      {weekDays.map((day, index) => (
                        <div
                          key={day}
                          className={`text-center font-bold py-2 text-xs sm:text-sm ${
                            index === 0 ? 'text-red-500' : index === 6 ? 'text-teal-500' : 'text-gray-600 dark:text-gray-400'
                          }`}
                        >
                          {day}
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-1.5">
                      {days.map((date, index) => {
                        const daySlots = getSlotsForDate(date);
                        const isToday = date && date.toDateString() === new Date().toDateString();
                        const isSunday = date && date.getDay() === 0;
                        const isSaturday = date && date.getDay() === 6;
                        return (
                          <div
                            key={index}
                            className={`min-h-[100px] sm:min-h-[120px] border rounded-xl p-1.5 transition-colors ${
                              date
                                ? 'bg-white dark:bg-gray-750 border-gray-200 dark:border-gray-600 hover:border-teal-300 dark:hover:border-teal-600'
                                : 'bg-gray-50/50 dark:bg-gray-800/50 border-transparent'
                            } ${isToday ? 'ring-2 ring-teal-500 border-teal-300' : ''}`}
                          >
                            {date && (
                              <>
                                <div className="flex items-center justify-between mb-1">
                                  <span
                                    className={`text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full ${
                                      isToday
                                        ? 'bg-teal-500 text-white'
                                        : isSunday
                                        ? 'text-red-500'
                                        : isSaturday
                                        ? 'text-teal-600 dark:text-teal-400'
                                        : 'text-gray-700 dark:text-gray-300'
                                    }`}
                                  >
                                    {date.getDate()}
                                  </span>
                                  <button
                                    onClick={() => openAddModal(date)}
                                    className="w-5 h-5 flex items-center justify-center hover:bg-teal-100 dark:hover:bg-teal-900/30 rounded transition-colors cursor-pointer"
                                    title="予定を追加"
                                  >
                                    <i className="ri-add-line text-teal-600 dark:text-teal-400 text-xs"></i>
                                  </button>
                                </div>
                                <div className="space-y-0.5">
                                  {daySlots.map((slot) => (
                                    <div
                                      key={slot.id}
                                      onClick={() => { setSelectedSlot(slot); setShowDetailModal(true); }}
                                      className="text-[10px] sm:text-xs p-1 rounded cursor-pointer hover:opacity-80 transition-opacity"
                                      style={{ backgroundColor: slot.color + '25', borderLeft: `3px solid ${slot.color}` }}
                                    >
                                      <div className="font-semibold text-gray-900 dark:text-white truncate leading-tight">{slot.title}</div>
                                      <div className="text-gray-500 dark:text-gray-400 leading-tight">
                                        {formatTime(slot.start_datetime)}〜{formatTime(slot.end_datetime)}
                                      </div>
                                      <div className="text-gray-400 dark:text-gray-500 leading-tight truncate">{slot.creator_name}</div>
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* スマホ用フルスクリーンチャット */}
          {showChat && (
            <div className="sm:hidden absolute inset-0 bg-gray-50 dark:bg-gray-800 flex flex-col z-10">
              <div className="p-3 border-b dark:border-gray-700 bg-white dark:bg-gray-800 shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowChat(false)}
                      className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <i className="ri-arrow-left-line text-lg text-gray-600 dark:text-gray-300"></i>
                    </button>
                    <i className="ri-chat-3-line text-teal-500 text-lg"></i>
                    <h3 className="font-bold text-gray-900 dark:text-white text-sm">グループチャット</h3>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{messages.length}件</p>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <i className="ri-chat-3-line text-4xl text-gray-300 dark:text-gray-600 mb-2"></i>
                    <p className="text-sm text-gray-500 dark:text-gray-400">まだメッセージがありません</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">最初のメッセージを送信しましょう！</p>
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isOwn = msg.user_id === myProfileId;
                    const readCount = getReadCount(msg.id, msg.user_id);
                    const displayName = profileMap.get(msg.user_id) || 'ユーザー';
                    return (
                      <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] flex flex-col gap-1 ${isOwn ? 'items-end' : 'items-start'}`}>
                          {!isOwn && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 px-2">{displayName}</span>
                          )}
                          <div className="relative group">
                            <div
                              className={`px-3 py-2 rounded-2xl text-sm break-words ${
                                isOwn
                                  ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-br-sm'
                                  : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 rounded-bl-sm'
                              }`}
                            >
                              {msg.message}
                            </div>
                            {isOwn && (
                              <button
                                onClick={() => handleDeleteMessage(msg.id, msg.user_id)}
                                className="absolute -left-8 top-1/2 -translate-y-1/2 p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-all cursor-pointer"
                                title="削除"
                              >
                                <i className="ri-delete-bin-line text-red-500 text-sm"></i>
                              </button>
                            )}
                          </div>
                          <div className={`flex items-center gap-1.5 px-2 ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}>
                            <span className="text-[10px] text-gray-400 dark:text-gray-500">
                              {new Date(msg.created_at).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            {isOwn && memberCount > 0 && (
                              <span className={`text-[10px] font-medium ${readCount >= memberCount ? 'text-teal-500' : 'text-gray-400 dark:text-gray-500'}`}>
                                {readCount >= memberCount ? '既読' : `既読 ${readCount}`}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="p-3 border-t dark:border-gray-700 bg-white dark:bg-gray-800 shrink-0">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                    placeholder="メッセージを入力..."
                    className="flex-1 px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white text-sm"
                    disabled={sendingMessage}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || sendingMessage}
                    className="px-4 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl hover:from-teal-600 hover:to-emerald-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer shrink-0"
                  >
                    {sendingMessage ? (
                      <i className="ri-loader-4-line animate-spin"></i>
                    ) : (
                      <i className="ri-send-plane-fill"></i>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* PC用サイドチャットパネル */}
          <div className="hidden sm:flex flex-col w-80 border-l dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
            <div className="p-4 border-b dark:border-gray-700 bg-white dark:bg-gray-800 shrink-0">
              <div className="flex items-center gap-2">
                <i className="ri-chat-3-line text-teal-500 text-lg"></i>
                <h3 className="font-bold text-gray-900 dark:text-white text-sm">グループチャット</h3>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{messages.length}件のメッセージ</p>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <i className="ri-chat-3-line text-4xl text-gray-300 dark:text-gray-600 mb-2"></i>
                  <p className="text-sm text-gray-500 dark:text-gray-400">まだメッセージがありません</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">最初のメッセージを送信しましょう！</p>
                </div>
              ) : (
                messages.map((msg) => {
                  const isOwn = msg.user_id === myProfileId;
                  const readCount = getReadCount(msg.id, msg.user_id);
                  const displayName = profileMap.get(msg.user_id) || 'ユーザー';
                  return (
                    <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] flex flex-col gap-1 ${isOwn ? 'items-end' : 'items-start'}`}>
                        {!isOwn && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 px-2">{displayName}</span>
                        )}
                        <div className="relative group">
                          <div
                            className={`px-3 py-2 rounded-2xl text-sm break-words ${
                              isOwn
                                ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-br-sm'
                                : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 rounded-bl-sm'
                            }`}
                          >
                            {msg.message}
                          </div>
                          {isOwn && (
                            <button
                              onClick={() => handleDeleteMessage(msg.id, msg.user_id)}
                              className="absolute -left-8 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-all cursor-pointer"
                              title="削除"
                            >
                              <i className="ri-delete-bin-line text-red-500 text-sm"></i>
                            </button>
                          )}
                        </div>
                        <div className={`flex items-center gap-1.5 px-2 ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}>
                          <span className="text-[10px] text-gray-400 dark:text-gray-500">
                            {new Date(msg.created_at).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          {isOwn && memberCount > 0 && (
                            <span className={`text-[10px] font-medium ${readCount >= memberCount ? 'text-teal-500' : 'text-gray-400 dark:text-gray-500'}`}>
                              {readCount >= memberCount ? '既読' : `既読 ${readCount}`}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="p-3 border-t dark:border-gray-700 bg-white dark:bg-gray-800 shrink-0">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                  placeholder="メッセージを入力..."
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white text-sm"
                  disabled={sendingMessage}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || sendingMessage}
                  className="px-4 py-2 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl hover:from-teal-600 hover:to-emerald-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer shrink-0"
                >
                  {sendingMessage ? (
                    <i className="ri-loader-4-line animate-spin"></i>
                  ) : (
                    <i className="ri-send-plane-fill"></i>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== 予定追加モーダル ===== */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[92vh] overflow-y-auto">
            <div className="sm:hidden flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
            </div>
            <div className="p-4 sm:p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <i className="ri-calendar-check-line text-teal-500"></i>予定を追加
                </h3>
                <button
                  onClick={() => { setShowAddModal(false); setNewSlot({ title: '', date: '', startHour: '9', startMinute: '0', endHour: '10', endMinute: '0', color: '#14B8A6', memo: '' }); }}
                  className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-lg text-gray-500"></i>
                </button>
              </div>
              {addErrorMsg && (
                <div className="mb-3 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg text-sm text-red-600 dark:text-red-400">
                  {addErrorMsg}
                </div>
              )}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">タイトル</label>
                  <input
                    type="text"
                    value={newSlot.title}
                    onChange={(e) => setNewSlot({ ...newSlot, title: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white text-sm"
                    placeholder="例：数学の勉強会"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">日付</label>
                  <input
                    type="date"
                    value={newSlot.date}
                    onChange={(e) => setNewSlot({ ...newSlot, date: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">開始時刻</label>
                  <TimeSelect
                    hourVal={newSlot.startHour}
                    minuteVal={newSlot.startMinute}
                    onHourChange={(v) => setNewSlot({ ...newSlot, startHour: v })}
                    onMinuteChange={(v) => setNewSlot({ ...newSlot, startMinute: v })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">終了時刻</label>
                  <TimeSelect
                    hourVal={newSlot.endHour}
                    minuteVal={newSlot.endMinute}
                    onHourChange={(v) => setNewSlot({ ...newSlot, endHour: v })}
                    onMinuteChange={(v) => setNewSlot({ ...newSlot, endMinute: v })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">カラー</label>
                  <div className="flex flex-wrap gap-2">
                    {COLORS.map((color) => (
                      <button
                        key={color.value}
                        onClick={() => setNewSlot({ ...newSlot, color: color.value })}
                        className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg transition-transform cursor-pointer ${
                          newSlot.color === color.value ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'hover:scale-105'
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.label}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">メモ（任意）</label>
                  <textarea
                    value={newSlot.memo}
                    onChange={(e) => setNewSlot({ ...newSlot, memo: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white text-sm resize-none"
                    placeholder="詳細や備考を入力してください"
                    rows={2}
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => { setShowAddModal(false); setNewSlot({ title: '', date: '', startHour: '9', startMinute: '0', endHour: '10', endMinute: '0', color: '#14B8A6', memo: '' }); }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium cursor-pointer"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleAddSlot}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl hover:from-teal-600 hover:to-emerald-600 transition-all text-sm font-medium cursor-pointer shadow-sm"
                >
                  追加
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== 予定編集モーダル ===== */}
      {showEditModal && selectedSlot && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[92vh] overflow-y-auto">
            <div className="sm:hidden flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
            </div>
            <div className="p-4 sm:p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <i className="ri-edit-line text-teal-500"></i>予定を編集
                </h3>
                <button
                  onClick={() => { setShowEditModal(false); setSelectedSlot(null); }}
                  className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer shrink-0"
                >
                  <i className="ri-close-line text-lg text-gray-500"></i>
                </button>
              </div>
              {editErrorMsg && (
                <div className="mb-3 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg text-sm text-red-600 dark:text-red-400">
                  {editErrorMsg}
                </div>
              )}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">タイトル</label>
                  <input
                    type="text"
                    value={editSlot.title}
                    onChange={(e) => setEditSlot({ ...editSlot, title: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white text-sm"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">日付</label>
                  <input
                    type="date"
                    value={editSlot.date}
                    onChange={(e) => setEditSlot({ ...editSlot, date: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">開始時刻</label>
                  <TimeSelect
                    hourVal={editSlot.startHour}
                    minuteVal={editSlot.startMinute}
                    onHourChange={(v) => setEditSlot({ ...editSlot, startHour: v })}
                    onMinuteChange={(v) => setEditSlot({ ...editSlot, startMinute: v })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">終了時刻</label>
                  <TimeSelect
                    hourVal={editSlot.endHour}
                    minuteVal={editSlot.endMinute}
                    onHourChange={(v) => setEditSlot({ ...editSlot, endHour: v })}
                    onMinuteChange={(v) => setEditSlot({ ...editSlot, endMinute: v })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">カラー</label>
                  <div className="flex flex-wrap gap-2">
                    {COLORS.map((color) => (
                      <button
                        key={color.value}
                        onClick={() => setEditSlot({ ...editSlot, color: color.value })}
                        className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg transition-transform cursor-pointer ${
                          editSlot.color === color.value ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'hover:scale-105'
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.label}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">メモ（任意）</label>
                  <textarea
                    value={editSlot.memo}
                    onChange={(e) => setEditSlot({ ...editSlot, memo: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white text-sm resize-none"
                    rows={2}
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => { setShowEditModal(false); setSelectedSlot(null); }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium cursor-pointer"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleEditSlot}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl hover:from-teal-600 hover:to-emerald-600 transition-all text-sm font-medium cursor-pointer shadow-sm"
                >
                  保存
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== 予定詳細モーダル ===== */}
      {showDetailModal && selectedSlot && (
        <div
          className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4"
          onClick={() => { setShowDetailModal(false); setSelectedSlot(null); }}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sm:hidden flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
            </div>
            <div className="p-4 sm:p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className="w-4 h-4 rounded shrink-0 mt-1" style={{ backgroundColor: selectedSlot.color }} />
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white break-words">{selectedSlot.title}</h3>
                </div>
                <button
                  onClick={() => { setShowDetailModal(false); setSelectedSlot(null); }}
                  className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer shrink-0 ml-2"
                >
                  <i className="ri-close-line text-lg text-gray-500"></i>
                </button>
              </div>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-start gap-3">
                  <i className="ri-calendar-line text-teal-500 text-lg mt-0.5 shrink-0"></i>
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">日付</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{formatDate(selectedSlot.start_datetime)}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-time-line text-teal-500 text-lg mt-0.5 shrink-0"></i>
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">時間</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatTime(selectedSlot.start_datetime)} 〜 {formatTime(selectedSlot.end_datetime)}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-user-line text-teal-500 text-lg mt-0.5 shrink-0"></i>
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">投稿者</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{selectedSlot.creator_name}</div>
                  </div>
                </div>
                {selectedSlot.memo && (
                  <div className="flex items-start gap-3">
                    <i className="ri-file-text-line text-teal-500 text-lg mt-0.5 shrink-0"></i>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">メモ</div>
                      <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                        {selectedSlot.memo}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* 本人のみ編集・削除ボタンを表示 */}
              {isMySlot(selectedSlot) && (
                <div className="mt-5 sm:mt-6 pt-4 border-t dark:border-gray-700 flex gap-3">
                  <button
                    onClick={() => openEditModal(selectedSlot)}
                    className="flex-1 px-4 py-2.5 bg-teal-500 hover:bg-teal-600 text-white rounded-xl transition-colors text-sm font-medium cursor-pointer flex items-center justify-center gap-2"
                  >
                    <i className="ri-edit-line"></i>
                    編集
                  </button>
                  <button
                    onClick={() => handleDeleteSlot(selectedSlot.id, selectedSlot.created_by)}
                    className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl transition-colors text-sm font-medium cursor-pointer flex items-center justify-center gap-2"
                  >
                    <i className="ri-delete-bin-line"></i>
                    削除
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== カレンダー名編集モーダル ===== */}
      {showEditNameModal && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md">
            <div className="sm:hidden flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
            </div>
            <div className="p-4 sm:p-5">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-4">カレンダー名を編集</h3>
              {nameErrorMsg && (
                <div className="mb-3 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg text-sm text-red-600 dark:text-red-400">
                  {nameErrorMsg}
                </div>
              )}
              <input
                type="text"
                value={newCalendarName}
                onChange={(e) => setNewCalendarName(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white text-sm mb-4"
                placeholder="カレンダー名を入力"
                autoFocus
              />
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowEditNameModal(false); setNewCalendarName(localCalendarName); }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium cursor-pointer"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleUpdateCalendarName}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl hover:from-teal-600 hover:to-emerald-600 transition-all text-sm font-medium cursor-pointer shadow-sm"
                >
                  保存
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
